import {
    createId,
    nowIso,
    getState,
    mutate,
    findUnit,
    delay,
    UNIT_TYPES,
} from './store';
import coursesApi from '@/utils/courses';
import { matchesRule } from '@/utils/corporate/accessRules';

export { matchesRule };

function courseName(course) {
    return course?.comercialName || course?.commercialName || course?.name || `Curso ${course?.id}`;
}

function normalizeCoursesResponse(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.courses)) return data.courses;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

async function fetchPlatformCourses(accessToken) {
    try {
        const data = await coursesApi.getDataCourse(accessToken);
        return normalizeCoursesResponse(data)
            .filter((course) => course && course.id != null)
            .slice(0, 12);
    } catch (e) {
        console.warn('[corporate] falha ao carregar cursos da plataforma, usando mock', e);
        return [];
    }
}

function syncCoursesIntoStore(platformCourses) {
    if (!platformCourses.length) return getState().courses || [];

    mutate((draft) => {
        draft.courses = platformCourses.map((course) => ({
            id: String(course.id),
            name: courseName(course),
            status: String(course.status) === '4' ? 'inactive' : 'active',
            imageFinalPath: course.imageFinalPath || course.imageFinalPath2 || null,
            moduleQty: course.moduleQty || 0,
            lessonQty: course.lessonQty || 0,
            raw: {
                id: course.id,
                comercialName: courseName(course),
                status: course.status,
            },
        }));
    });

    return getState().courses;
}

export function normalizeAssignment(raw, snapshot = getState()) {
    if (!raw) return null;

    if (raw.resourceType && raw.resourceId != null && raw.sectorId != null) {
        return {
            ...raw,
            resourceType: raw.resourceType === 'group' ? 'group' : 'course',
            resourceId: String(raw.resourceId),
            sectorId: raw.sectorId,
            roleId: raw.roleId || null,
            courseId: raw.resourceType === 'course'
                ? String(raw.resourceId)
                : (raw.courseId ? String(raw.courseId) : null),
            status: raw.status || 'active',
        };
    }

    // Legacy: organizationalUnitId + courseId
    const unit = raw.organizationalUnitId
        ? findUnit(raw.organizationalUnitId, snapshot)
        : null;
    let sectorId = null;
    let roleId = null;
    if (unit?.type === UNIT_TYPES.ROLE) {
        roleId = unit.id;
        sectorId = unit.parentId || null;
    } else if (
        unit?.type === UNIT_TYPES.SECTOR
        || unit?.type === UNIT_TYPES.BOARD
    ) {
        sectorId = unit.id;
    }

    return {
        ...raw,
        resourceType: 'course',
        resourceId: String(raw.courseId || raw.resourceId || ''),
        courseId: String(raw.courseId || raw.resourceId || ''),
        sectorId,
        roleId,
        status: raw.status || 'active',
    };
}

function countImpactedUsers(assignment, snapshot) {
    if (assignment.userId) {
        return snapshot.users.some((u) => u.id === assignment.userId) ? 1 : 0;
    }
    const areaId = assignment.sectorId;
    if (!areaId) return 0;

    return snapshot.users.filter((u) => {
        if (u.status === 'inactive') return false;
        return matchesRule(assignment, {
            sectorId: u.sectorId,
            roleId: u.roleId,
            boardId: u.boardId,
        });
    }).length;
}

function enrichAssignment(assignment, snapshot = getState()) {
    const normalized = normalizeAssignment(assignment, snapshot);
    if (!normalized) return null;

    const area = normalized.sectorId
        ? findUnit(normalized.sectorId, snapshot)
        : null;
    const role = normalized.roleId
        ? findUnit(normalized.roleId, snapshot)
        : null;

    let resourceName = normalized.resourceId;
    let resourceStatus = 'active';

    if (normalized.resourceType === 'group') {
        const group = (snapshot.courseGroups || []).find(
            (g) => String(g.id) === String(normalized.resourceId),
        );
        resourceName = group?.name || normalized.resourceId;
        resourceStatus = group?.status || 'active';
    } else {
        const course = (snapshot.courses || []).find(
            (c) => String(c.id) === String(normalized.resourceId),
        );
        resourceName = course?.name || normalized.resourceId;
        resourceStatus = course?.status || 'active';
    }

    return {
        id: normalized.id,
        resourceType: normalized.resourceType,
        resourceId: String(normalized.resourceId),
        sectorId: normalized.sectorId || null,
        roleId: normalized.roleId || null,
        mandatory: Boolean(normalized.mandatory),
        deadlineDays: normalized.deadlineDays ?? null,
        dateInsert: normalized.dateInsert || normalized.startsAt || null,
        dateExpire: normalized.dateExpire || normalized.endsAt || null,
        status: resourceStatus === 'inactive' ? 'blocked' : (normalized.status || 'active'),
        resourceName,
        sectorName: area?.name || null,
        roleName: role?.name || null,
        impactedUsers: countImpactedUsers(normalized, snapshot),
    };
}

function seedAssignmentsFromCourses(draft) {
    const courses = draft.courses || [];
    const groups = draft.courseGroups || [];
    const sectors = draft.units.filter(
        (u) => (u.type === UNIT_TYPES.SECTOR || u.type === UNIT_TYPES.BOARD)
            && u.status === 'active',
    );
    const roles = draft.units.filter(
        (u) => u.type === UNIT_TYPES.ROLE && u.status === 'active',
    );

    if (!sectors.length && !roles.length) {
        draft.courseAssignments = [];
        return;
    }

    const sectorDev = sectors.find((s) => s.id === 'unit_sector_dev') || sectors[0];
    const sectorInfra = sectors.find((s) => s.id === 'unit_sector_infra') || sectors[1] || sectorDev;
    const roleJr = roles.find((r) => r.id === 'unit_role_dev_jr') || roles[0];

    const rows = [];

    if (courses[0] && sectorDev && roleJr) {
        rows.push({
            id: 'assign_1',
            companyId: draft.companyId,
            resourceType: 'course',
            resourceId: String(courses[0].id),
            courseId: String(courses[0].id),
            sectorId: sectorDev.id,
            roleId: roleJr.id,
            userId: null,
            mandatory: true,
            deadlineDays: 30,
            startsAt: nowIso(),
            endsAt: null,
            status: 'active',
            createdBy: 'user_mgr_1',
            createdAt: nowIso(),
            updatedAt: nowIso(),
        });
    }

    const onboarding = groups.find((g) => g.id === 'group_onboarding') || groups[0];
    if (onboarding && sectorDev) {
        rows.push({
            id: createId('assign'),
            companyId: draft.companyId,
            resourceType: 'group',
            resourceId: String(onboarding.id),
            courseId: null,
            sectorId: sectorDev.id,
            roleId: null,
            userId: null,
            mandatory: true,
            deadlineDays: 45,
            startsAt: nowIso(),
            endsAt: null,
            status: 'active',
            createdBy: 'user_mgr_1',
            createdAt: nowIso(),
            updatedAt: nowIso(),
        });
    }

    const security = groups.find((g) => g.id === 'group_security');
    if (security) {
        [sectorDev, sectorInfra].filter(Boolean).forEach((sector) => {
            rows.push({
                id: createId('assign'),
                companyId: draft.companyId,
                resourceType: 'group',
                resourceId: String(security.id),
                courseId: null,
                sectorId: sector.id,
                roleId: null,
                userId: null,
                mandatory: true,
                deadlineDays: 60,
                startsAt: nowIso(),
                endsAt: null,
                status: 'active',
                createdBy: 'user_mgr_1',
                createdAt: nowIso(),
                updatedAt: nowIso(),
            });
        });
    }

    draft.courseAssignments = rows;
}

function ensureNormalizedAssignments(draft) {
    draft.courseAssignments = (draft.courseAssignments || [])
        .map((a) => normalizeAssignment(a, draft))
        .filter((a) => a && a.resourceId && a.sectorId);
}

function ensureSeededAssignments(platformCourses) {
    syncCoursesIntoStore(platformCourses);

    return mutate((draft) => {
        ensureNormalizedAssignments(draft);

        const hasNewShape = (draft.courseAssignments || []).some(
            (a) => a.resourceType && a.sectorId,
        );

        if (!hasNewShape || !(draft.courseAssignments || []).length) {
            seedAssignmentsFromCourses(draft);
        }

        return draft.courseAssignments.map((a) => enrichAssignment(a, draft)).filter(Boolean);
    });
}

export default {
    matchesRule,
    normalizeAssignment,
    enrichAssignment,

    async list(accessToken, filters = {}) {
        await delay();
        const platformCourses = await fetchPlatformCourses(accessToken);
        let rows = ensureSeededAssignments(platformCourses);

        if (filters.search) {
            const term = String(filters.search).toLowerCase();
            rows = rows.filter(
                (row) => (row.resourceName || '').toLowerCase().includes(term)
                    || (row.sectorName || '').toLowerCase().includes(term)
                    || (row.roleName || '').toLowerCase().includes(term),
            );
        }
        if (filters.sectorId) {
            rows = rows.filter((row) => row.sectorId === filters.sectorId);
        }
        if (filters.roleId) {
            rows = rows.filter((row) => row.roleId === filters.roleId);
        }
        if (filters.resourceType) {
            rows = rows.filter((row) => row.resourceType === filters.resourceType);
        }
        if (filters.mandatory === true || filters.mandatory === false) {
            rows = rows.filter((row) => row.mandatory === filters.mandatory);
        }

        const snapshot = getState();
        return {
            content: rows,
            totalElements: rows.length,
            page: 0,
            size: rows.length,
            coursesAvailable: snapshot.courses || [],
            groupsAvailable: snapshot.courseGroups || [],
        };
    },

    async preview(accessToken, payload) {
        await delay();
        const snapshot = getState();
        const fake = normalizeAssignment({
            ...payload,
            id: 'preview',
            status: 'active',
            resourceType: payload.resourceType || 'course',
            resourceId: String(payload.resourceId || payload.courseId || ''),
            sectorId: payload.sectorId,
            roleId: payload.roleId || null,
        }, snapshot);
        const count = countImpactedUsers(fake, snapshot);
        return {
            impactedUsers: count,
            message: `Este vínculo alcança ${count} colaboradores.`,
        };
    },

    async create(accessToken, payload) {
        await delay();
        return mutate((draft) => {
            const resourceType = payload.resourceType === 'group' ? 'group' : 'course';
            const resourceId = String(payload.resourceId || payload.courseId || '');
            if (!resourceId || !payload.sectorId) {
                const error = new Error('Informe o recurso e a área.');
                error.code = 'VALIDATION';
                throw error;
            }
            const assignment = {
                id: createId('assign'),
                companyId: draft.companyId,
                resourceType,
                resourceId,
                courseId: resourceType === 'course' ? resourceId : null,
                sectorId: payload.sectorId,
                roleId: payload.roleId || null,
                userId: payload.userId || null,
                mandatory: Boolean(payload.mandatory),
                deadlineDays: payload.deadlineDays ?? null,
                startsAt: payload.startsAt || nowIso(),
                endsAt: payload.endsAt || null,
                status: 'active',
                createdBy: 'user_mgr_1',
                createdAt: nowIso(),
                updatedAt: nowIso(),
            };
            draft.courseAssignments.push(assignment);
            return enrichAssignment(assignment, draft);
        });
    },

    async update(accessToken, id, payload) {
        await delay();
        return mutate((draft) => {
            const index = draft.courseAssignments.findIndex((a) => a.id === id);
            if (index < 0) {
                const error = new Error('Vínculo não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            const current = normalizeAssignment(draft.courseAssignments[index], draft);
            const resourceType = payload.resourceType === 'group'
                ? 'group'
                : (payload.resourceType === 'course' ? 'course' : current.resourceType);
            const resourceId = String(
                payload.resourceId || payload.courseId || current.resourceId,
            );
            const next = {
                ...current,
                ...payload,
                resourceType,
                resourceId,
                courseId: resourceType === 'course' ? resourceId : null,
                sectorId: payload.sectorId != null ? payload.sectorId : current.sectorId,
                roleId: payload.roleId === undefined ? current.roleId : (payload.roleId || null),
                mandatory: payload.mandatory !== undefined
                    ? Boolean(payload.mandatory)
                    : current.mandatory,
                deadlineDays: payload.deadlineDays !== undefined
                    ? payload.deadlineDays
                    : current.deadlineDays,
                updatedAt: nowIso(),
            };
            draft.courseAssignments[index] = next;
            return enrichAssignment(next, draft);
        });
    },

    async remove(accessToken, id) {
        await delay();
        return mutate((draft) => {
            draft.courseAssignments = draft.courseAssignments.filter((a) => a.id !== id);
            return { success: true };
        });
    },

    async reseed(accessToken) {
        await delay();
        const platformCourses = await fetchPlatformCourses(accessToken);
        syncCoursesIntoStore(platformCourses);
        return mutate((draft) => {
            seedAssignmentsFromCourses(draft);
            return draft.courseAssignments.map((a) => enrichAssignment(a, draft)).filter(Boolean);
        });
    },

    listMatchingForViewer({ sectorId, roleId, boardId, resourceType } = {}, snapshot = getState()) {
        return (snapshot.courseAssignments || [])
            .map((a) => normalizeAssignment(a, snapshot))
            .filter(Boolean)
            .filter((a) => !resourceType || a.resourceType === resourceType)
            .filter((a) => matchesRule(a, { sectorId, roleId, boardId }));
    },
};
